/**
 * src/lib/marketing/pixel-client.js
 *
 * Cliente-side helper para disparar eventos del Meta Pixel (window.fbq)
 * desde cualquier componente del funnel.
 *
 * Cómo se relaciona con el resto del stack de marketing:
 *   - Capa de TRANSPORTE (low-level): src/lib/capi.js
 *       Hash PII, timeout, retry. Server-side only.
 *   - Capa de EVENTOS CAPI (high-level): src/lib/marketing/capi-events.js
 *       Mapea plan_id → $, manda Subscribe/InitiateCheckout/etc al CAPI
 *       desde webhooks. Server-side.
 *   - Capa CLIENT-SIDE (este archivo):
 *       Dispara los mismos eventos via fbq() en el navegador para que
 *       Meta Business Manager los detecte en "Probar eventos" y permita
 *       configurar conversiones desde la UI. Es complementaria al CAPI:
 *       Meta hace dedup automático cuando el mismo evento llega por
 *       ambos canales.
 *
 * Por qué client + server (no solo CAPI):
 *   - CAPI es más confiable contra ad blockers / iOS ITP / Safari ITP.
 *   - PERO Meta Business Manager UI ("Probar eventos", "Configurar y
 *     agregar eventos") solo detecta el evento si llega también por
 *     fbq() client-side.
 *
 * Filosofía:
 *   - SSR-safe: chequea window antes de tocar nada.
 *   - Consent-aware: si MetaPixel.jsx no cargó fbq (consent denied o
 *     env var faltante), window.fbq es undefined → no-op silencioso.
 *
 * Nota sobre ubicación: este archivo es .js puro (sin JSX) a propósito —
 * permite testearlo desde tests/unit sin arrastrar el bundle de React/Next.
 * MetaPixel.jsx re-exporta `trackFbq` para que los imports legacy desde
 * '@/components/analytics/MetaPixel' sigan funcionando.
 */

/**
 * Llama `window.fbq(action, eventName, params)` de forma segura.
 * No-op si SSR o si fbq no cargó (consent denied / ad blocker).
 *
 * @param {'track'|'trackCustom'} action
 * @param {string} eventName - Standard event: ViewContent, Lead,
 *   InitiateCheckout, StartTrial, Subscribe, CompleteRegistration
 * @param {Record<string, any>} [params]
 */
export function trackFbq(action, eventName, params) {
  if (typeof window === 'undefined') return;
  if (typeof window.fbq !== 'function') return;
  window.fbq(action, eventName, params);
}
